// @ts-strict-ignore
import React from 'react';
import _ from 'lodash';
import { bindingsDefinition, injected, prop } from '@/hybrid/core/bindings.util';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import { addAssetOrItem, setIsLoading } from '@/hybrid/assetGroupEditor/assetGroup.actions';
import { CREATED_BY_SEEQ_WORKBENCH, IS_PROTRACTOR } from '@/main/app.constants';
import { IconWithManagedSpinner } from '@/hybrid/core/IconWithManagedSpinner.atom';
import { useFluxPath } from '@/hybrid/core/hooks/useFluxPath.hook';
import { WorksheetActions } from '@/worksheet/worksheet.actions';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { isAsset } from '@/hybrid/utilities/utilities';
import { sqAssetGroupStore, sqLicenseManagementStore, sqWorksheetStore } from '@/core/core.stores';
import { editAssetGroup, validateItemToAdd } from '@/hybrid/assetGroupEditor/assetGroup.utilities';
import { canWriteItem } from '@/services/authorization.service';

const searchResultAssetIconsBindings = bindingsDefinition({
  item: prop<any>(),
  isAssetGroupView: prop<boolean>(),
  sqWorksheetActions: injected<WorksheetActions>(),
  $state: injected<ng.ui.IStateService>(),
});

export const SearchResultAssetIcons: SeeqComponent<typeof searchResultAssetIconsBindings> = ({
  item,
  isAssetGroupView,
}) => {
  const { sqWorksheetActions, $state } = useInjectedBindings(searchResultAssetIconsBindings);
  const storeAssets = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.assets);
  const isEditingAssetGroup = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.id === item.id);
  const isEditAllowed =
    isAsset(item) &&
    sqLicenseManagementStore.hasAssetGroups() &&
    canWriteItem(item) &&
    ((_.find(item.properties, { name: SeeqNames.Properties.TreeType }) as any)?.value === CREATED_BY_SEEQ_WORKBENCH ||
      item.isAssetGroupItem) &&
    _.isEmpty(item.ancestors);

  const addAsset = (addMatchingItemsOnly = false, addChildrenOfItemsOfItems = false) => {
    setIsLoading(true);
    const promise = validateItemToAdd({ item })
      ? addAssetOrItem({
          assetOrItem: item,
          addMatchingItemsOnly,
          addItemsOfItemsChildren: addChildrenOfItemsOfItems,
          $state,
        })
      : Promise.resolve();

    return promise.finally(() => setIsLoading(false));
  };

  const loadAssetGroupForEdit = (): any => {
    sqWorksheetActions.setReturnViewKey(sqWorksheetStore.view.key);
    return editAssetGroup(item, sqWorksheetActions);
  };
  return (
    <>
      {isEditAllowed && (
        <IconWithManagedSpinner
          icon="fa-pencil"
          testId="editAssetGroupIcon"
          disabled={isEditingAssetGroup}
          large={true}
          correctSpin={false}
          extraClassNames="searchBtn"
          action={loadAssetGroupForEdit}
          actionOnEnter={true}
          tooltip={!isEditingAssetGroup && 'SEARCH_DATA.EDIT_ASSET_GROUP_TOOLTIP'}
          tooltipDelay={500}
        />
      )}

      {isAssetGroupView && (
        <>
          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasChildren)) && (
            <IconWithManagedSpinner
              icon="fc-plus-assets"
              extraClassNames="searchBtn"
              action={() => addAsset(false, !isAsset(item))}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIcon"
              tooltip={!isEditingAssetGroup && 'SEARCH_DATA.ADD_ASSET_TO_GROUP_TOOLTIP'}
              tooltipDelay={500}
            />
          )}

          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasChildren)) && !_.isEmpty(storeAssets) && (
            <IconWithManagedSpinner
              icon="fc-plus-selected"
              extraClassNames="searchBtn"
              action={() => addAsset(true)}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIconSameChildrenOnly"
              // TODO CRAB-29079: remove IS_PROTRACTOR check, if test still fails find a better solutions
              tooltip={
                !isEditingAssetGroup && !IS_PROTRACTOR && 'SEARCH_DATA.ADD_ASSET_TO_GROUP_CHILDREN_MATCH_TOOLTIP'
              }
              tooltipDelay={500}
            />
          )}

          {/* items (including items of items' parents) */}
          {!isAsset(item) && (
            <IconWithManagedSpinner
              icon="fa-plus-circle"
              extraClassNames="searchBtn"
              action={addAsset}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              testId="addItemToAssetIcon"
              tooltip="SEARCH_DATA.ADD_ASSET_ADD_ITEM_TOOLTIP"
              tooltipDelay={500}
            />
          )}
        </>
      )}
    </>
  );
};
